import { useState, useEffect } from 'react';
import { GALLERY_IMAGES, ALL_GALLERY_IMAGES } from '@lib/constants';

const PhotoGridSection = ({ activeFilter, onImageClick }) => {
  const [displayedImages, setDisplayedImages] = useState([]);
  const [loadedImages, setLoadedImages] = useState(new Set());

  useEffect(() => {
    // Get images based on active filter
    const images = activeFilter === 'all'
      ? ALL_GALLERY_IMAGES
      : GALLERY_IMAGES[activeFilter] || [];

    setDisplayedImages(images);
  }, [activeFilter]);

  const handleImageLoad = (index) => {
    setLoadedImages(prev => new Set([...prev, index]));
  };

  const handleImageClick = (image, index) => {
    onImageClick(image, displayedImages);
  };

  if (displayedImages.length === 0) {
    return (
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-7xl text-center">
          <p className="text-lg" style={{ color: 'var(--color-muted)' }}>
            No images found for this category.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 pb-20 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-7xl">
        {/* Mobile: horizontal scroll, Desktop: grid */}
        <div className="sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 sm:gap-4 flex sm:flex-none overflow-x-auto gap-3 snap-x snap-mandatory scrollbar-hide pb-4 sm:pb-0 -mx-4 px-4 sm:mx-0">
          {displayedImages.map((image, index) => (
            <div
              key={index}
              className="group relative overflow-hidden rounded-lg cursor-pointer transition-all duration-300 flex-shrink-0 w-[75vw] sm:w-auto snap-center"
              style={{
                backgroundColor: 'var(--color-card-bg)',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'var(--color-card-border)',
              }}
              onClick={() => handleImageClick(image, index)}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-card-hover-border)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 20px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-card-border)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Loading skeleton */}
              {!loadedImages.has(index) && (
                <div
                  className="absolute inset-0 animate-pulse"
                  style={{ backgroundColor: 'var(--color-theme-2)' }}
                />
              )}

              {/* Image */}
              <img
                src={image.src}
                alt={image.title}
                loading="lazy"
                className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-105"
                onLoad={() => handleImageLoad(index)}
                style={{
                  opacity: loadedImages.has(index) ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out'
                }}
              />

              {/* Overlay on hover */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4"
                style={{
                  background: 'linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent)'
                }}
              >
                <div>
                  <p className="text-white font-medium text-sm leading-tight">
                    {image.title}
                  </p>
                  <p
                    className="text-xs mt-1 capitalize"
                    style={{ color: 'rgba(255, 255, 255, 0.8)' }}
                  >
                    {image.category.replace('-', ' ')}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PhotoGridSection;
